import { Link } from 'react-router-dom';
import { isTerminalMatterStatus } from '@/lib/matterStatus';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  FileText,
  CalendarBlank,
  Wallet,
  ArrowRight,
  CircleNotch as Loader2,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchMyMatters,
  fetchMyCalendar,
  fetchMyFeeNotes,
  fetchMyDocuments,
} from '@/lib/solicitorPortalApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

export default function SolicitorDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firmName = user?.solicitorProfile?.firmName ?? 'your firm';

  const matters = useQuery({ queryKey: ['portal', 'matters'], queryFn: fetchMyMatters });
  const calendar = useQuery({ queryKey: ['portal', 'calendar'], queryFn: () => fetchMyCalendar() });
  const feeNotes = useQuery({ queryKey: ['portal', 'feeNotes'], queryFn: fetchMyFeeNotes });
  const documents = useQuery({ queryKey: ['portal', 'documents'], queryFn: () => fetchMyDocuments() });

  const openMatters = matters.data?.filter((m) => !m.closedDate).length ?? 0;
  const upcomingConferences = calendar.data?.conferences.length ?? 0;
  const upcomingHearings = calendar.data?.hearings.length ?? 0;
  const outstandingInvoices = feeNotes.data?.filter((f) => f.status !== 'paid').length ?? 0;

  // This is the client-facing portal: a solicitor told "no matters yet" and "0 outstanding fee
  // notes" over a failed fetch has been given four confident zeros about their own business.
  // Loading already showed an em dash; a failure must do the same and say why.
  const unknown = (q: { isLoading: boolean; isError: boolean }) => q.isLoading || q.isError;
  const couldNotLoad = (q: { isError: boolean }) => (q.isError ? 'could not load' : undefined);

  const recentDocsFromCounsel =
    documents.data
      ?.filter((d) => d.uploadedBy.memberType !== 'solicitor')
      .slice(0, 5) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-600 dark:text-brand-400">
          Solicitor Portal · {firmName}
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
          Welcome back, {user?.firstName}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everything you have with chambers, in one place.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Open matters"
          value={unknown(matters) ? '—' : openMatters}
          icon={<Briefcase className="h-4 w-4" />}
          to="/solicitor/matters"
          hint={couldNotLoad(matters)}
        />
        <StatCard
          label="Upcoming conferences"
          value={unknown(calendar) ? '—' : upcomingConferences}
          icon={<CalendarBlank className="h-4 w-4" />}
          to="/solicitor/calendar"
          hint={couldNotLoad(calendar) ?? (upcomingHearings > 0 ? `+${upcomingHearings} court hearing${upcomingHearings > 1 ? 's' : ''}` : undefined)}
        />
        <StatCard
          label="Outstanding fee notes"
          value={unknown(feeNotes) ? '—' : outstandingInvoices}
          icon={<Wallet className="h-4 w-4" />}
          to="/solicitor/fees"
          hint={couldNotLoad(feeNotes)}
        />
        <StatCard
          label="Documents"
          value={unknown(documents) ? '—' : documents.data?.length ?? 0}
          icon={<FileText className="h-4 w-4" />}
          to="/solicitor/documents"
          hint={couldNotLoad(documents)}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel
          title="New from counsel"
          action={{ label: 'All documents', to: '/solicitor/documents' }}
        >
          {documents.isLoading ? (
            <Loading />
          ) : documents.isError ? (
            <QueryErrorState compact what="your documents" onRetry={() => void documents.refetch()} />
          ) : recentDocsFromCounsel.length === 0 ? (
            <Empty text="No documents from counsel yet." />
          ) : (
            <ul className="divide-y divide-border">
              {recentDocsFromCounsel.map((d) => (
                <li key={d.id} className="flex items-center gap-3 py-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{d.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {d.case.caseNumber} · {d.uploadedBy.firstName} {d.uploadedBy.lastName} ·{' '}
                      {new Date(d.createdAt).toLocaleDateString('en-GB')}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Your matters"
          action={{ label: 'All matters', to: '/solicitor/matters' }}
        >
          {matters.isLoading ? (
            <Loading />
          ) : matters.isError ? (
            <QueryErrorState compact what="your matters" onRetry={() => void matters.refetch()} />
          ) : (matters.data?.length ?? 0) === 0 ? (
            <Empty text="No matters yet. Your clerk will notify you when one opens." />
          ) : (
            <ul className="divide-y divide-border">
              {matters.data!.slice(0, 5).map((m) => (
                <li key={m.id}>
                  <Link
                    to={`/solicitor/matters/${m.id}`}
                    className="flex items-center gap-3 py-3 hover:bg-muted/40 -mx-2 px-2 rounded-md"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {m.caseName}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {m.caseNumber} ·{' '}
                        {m.leadBarrister
                          ? `${m.leadBarrister.user.firstName} ${m.leadBarrister.user.lastName}`
                          : 'Awaiting allocation'}
                      </div>
                    </div>
                    <StatusPill status={m.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ─── Small helpers ─────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  to,
  hint,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  to: string;
  hint?: string;
}) {
  return (
    <Link
      to={to}
      className="group rounded-lg border border-border bg-card p-4 transition hover:border-brand-300 hover:shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-muted-foreground group-hover:text-brand-600 dark:group-hover:text-brand-400">{icon}</div>
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </Link>
  );
}

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {action && (
          <Link
            to={action.to}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
          >
            {action.label}
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      <div className="px-5 py-2">{children}</div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading…
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="py-6 text-sm text-muted-foreground">{text}</div>;
}

function StatusPill({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ');
  const tone =
    status === 'active' || status === 'intake'
      ? 'bg-brand-100 text-brand-700 dark:text-brand-300'
      : isTerminalMatterStatus(status)
      ? 'bg-muted text-muted-foreground'
      : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
  return (
    <span className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${tone}`}>
      {label}
    </span>
  );
}
